/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react-hooks/refs */
import "./QuickNews.css";

import QuickNewsContainer from "@components/Container/QuickNewsContainer";
import { QN_ANALYTICS_EVENT_BASE } from "@config/QuickNewsConstants";
import { formatDate } from "@helper/useDateFormat";
import { useTablaCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTablaCustomEventAnalyticsCall";
import { renderFeedCard } from "@modules/QuickNews/QnCardHelper";
import QnCardNav from "@modules/QuickNews/QnCardNav";
import QnFooter from "@modules/QuickNews/QnFooter";
import QnOnboard from "@modules/QuickNews/QnOnboard";
import QnTopNav from "@modules/QuickNews/QnTopNav";
import { Route } from "@route-types/QuickNews/+types/QuickNews";
import ErrorPage from "@routes/StatusHandler/StatusHandler";
import {
  fetchContext,
  ResponseType,
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { createMetaTags, type MetaTagsProps } from "@sphtech/web2-seo-schema";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import { QuickNewsDataPayload } from "@typings/PagePayloadType";
import { FeedItem } from "@typings/QuickNews";
import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import { getAuthorsName } from "../Article/ArticleMetaData";
import { getCardStyle } from "./helper/QnCardStyle";
import { setMetaInfo, useInfiniteContent } from "./helper/QuickNewsHelper";
import { fetchQNData } from "./QuickNews.server";
import QuickNewsMetaData from "./QuickNewsMetaData";

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    QuickNewsDataPayload,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const metaData = dataLoaderResponse.payload.metaTags as MetaTagsProps;
    return createMetaTags(metaData);
  }
}

export default function QuickNews(): ReactElement {
  const dataLoaderResponse: TRouteWithoutRedirect<
    QuickNewsDataPayload,
    string
  > = useLoaderData();

  // Call all hooks before any conditional returns
  const tablaCustomEvent = useTablaCustomEventAnalyticsCall();

  const isSuccess = dataLoaderResponse.type === ResponseType.SUCCESS;

  // Extract data safely for hook initialization (use defaults for error cases)
  const Stories = isSuccess ? dataLoaderResponse.payload.recentArticles : [];

  const initialId = isSuccess
    ? dataLoaderResponse.payload.initialId
    : undefined;

  const {
    content: articles,
    hasMore,
    loadMore,
    isInitialized,
    loadMoreOlder,
  } = useInfiniteContent({
    Stories,
    initialId,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const [articleIndex, setArticleIndex] = useState(0);
  // const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const isScrolling = useRef(false);
  const [onBoarded, setOnBoarded] = useState(!!initialId);

  const cards = articles;
  const currentIndex = articleIndex;
  const setCurrentIndex = setArticleIndex;

  const getClickLabel = useCallback(
    (card: FeedItem) => ("title" in card ? card.title : ""),
    [],
  );

  // Responsive
  useEffect(() => {
    // setMounted(true);

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Tab switching resets indices
  const setPushState = useCallback(
    (activeIndex?: number) => {
      const index = activeIndex ?? currentIndex;
      if (
        articles[index] &&
        !("adType" in articles[index] || "endCard" in articles[index])
      ) {
        const item = articles[index];
        window.history.pushState(
          "article",
          item.title,
          `/quick-news/${item.id}`,
        );
        setOnBoarded(true);
      }
    },
    [articles, currentIndex],
  );

  const setMetaTags = useCallback(
    (index?: number) => {
      const currentIndex = index ?? articleIndex;
      const card = articles[currentIndex];
      const { metaTags } = QuickNewsMetaData({
        card,
        onBoarded: true,
        pagination: currentIndex,
      });

      setMetaInfo(metaTags);
    },
    [articles, articleIndex],
  );

  const trackCardNavigation = useCallback(
    (direction: "previous" | "next", eventType?: string) => {
      const clickActionMap: Record<string, string> = {
        keyboard: "keyboard press",
        scroll: "mouse scroll",
        mouse: "mouse drag",
        touch: "touch swipe",
      };
      const clickActionType = clickActionMap[eventType || ""] || "click";
      const platform = eventType === "touch" ? "mobile" : "desktop";
      const nextIndex = articleIndex + 1;
      const currentTypeIndex =
        direction === "next" ? nextIndex : currentIndex - 1;
      const currentCard = articles[currentTypeIndex];
      const clickAction = `${platform}::${clickActionType} ${direction}::${direction == "next" ? currentTypeIndex : currentTypeIndex + 1}`;

      tablaCustomEvent({
        ...QN_ANALYTICS_EVENT_BASE,
        clickAction: clickAction,
        clickLabel: getClickLabel(currentCard),
      });

      if (!("adType" in articles[currentTypeIndex])) {
        tablaCustomEvent({
          event: "virtual_pv",
          articleid: articles[currentTypeIndex].id,
          cue_articleid: articles[currentTypeIndex].id,
          pagination: currentTypeIndex + 1,
          author:
            "authors" in articles[currentTypeIndex]
              ? getAuthorsName(articles[currentTypeIndex].authors || [])
              : "",
          level2: "quick-news",
          chapter1: "",
          contentcat: 1,
          contentType: undefined,
          pubdate:
            "createdDate" in articles[currentTypeIndex]
              ? formatDate(
                  articles[currentTypeIndex].createdDate,
                  "yyyy-MM-dd HH:mm:ss",
                )
              : "",
          title: articles[currentTypeIndex].title,
          virtual_url: `${window.location.origin}/quick-news/${articles[currentTypeIndex].id}`,
        });
      }
    },
    [articleIndex, currentIndex, articles, getClickLabel, tablaCustomEvent],
  );

  // Infinite content loading
  useEffect(() => {
    const fetchMoreFeed = async () => {
      if (articles.length - articleIndex === 3 && hasMore && isInitialized)
        await loadMore();
    };
    fetchMoreFeed().catch(console.error);
  }, [articleIndex, articles.length, loadMore, hasMore, isInitialized]);

  const getOlderArticles = useCallback(() => {
    loadMoreOlder();
    void loadMore();
  }, [loadMoreOlder, loadMore]);

  // Prevent pull-to-refresh and iOS bounce
  useEffect(() => {
    if (!isMobile) return;
    document.body.style.overscrollBehavior = "none";
    document.documentElement.style.overscrollBehavior = "none";
    const preventDefault = (e: TouchEvent) => {
      if (e.touches.length > 1) return; // Allow multi-touch gestures
      e.preventDefault();
    };
    document.addEventListener("touchmove", preventDefault, { passive: false });
    return () => {
      document.body.style.overscrollBehavior = "";
      document.documentElement.style.overscrollBehavior = "";
      document.removeEventListener("touchmove", preventDefault);
    };
  }, [isMobile]);

  // Card Navigation
  const prevCard = useCallback(
    (eventType?: string) => {
      if (articleIndex === 0) {
        window.location.href = "/quick-news";
        setOnBoarded(false);
        return;
      }

      setCurrentIndex((prev) => prev - 1);
      setPushState(currentIndex - 1);
      setMetaTags(currentIndex - 1);
      trackCardNavigation("previous", eventType);
    },
    [
      articleIndex,
      setCurrentIndex,
      trackCardNavigation,
      setPushState,
      currentIndex,
      setMetaTags,
    ],
  );

  const nextCard = useCallback(
    (eventType?: string) => {
      if (articleIndex >= articles.length - 1) return;
      setCurrentIndex((prev) => prev + 1);
      setPushState(currentIndex + 1);
      setMetaTags(currentIndex + 1);
      trackCardNavigation("next", eventType);
    },
    [
      articleIndex,
      articles.length,
      setCurrentIndex,
      trackCardNavigation,
      setPushState,
      currentIndex,
      setMetaTags,
    ],
  );

  // Drag/Touch state
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startX, setStartX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [offsetX, setOffsetX] = useState(0);

  // Handle keyboard navigation
  const handleStart = (clientY: number, clientX: number) => {
    setIsDragging(true);
    setStartY(clientY);
    setStartX(clientX);
  };
  const handleMove = (clientY: number, clientX: number) => {
    if (!isDragging) return;
    setOffsetY(clientY - startY);
    setOffsetX(clientX - startX);
  };

  const handleEnd = useCallback(
    (eventHandler: string) => {
      setIsDragging(false);
      const verticalThreshold = 30;
      if (Math.abs(offsetY) > Math.abs(offsetX)) {
        if (offsetY > verticalThreshold) {
          if (currentIndex === 0) {
            window.location.href = "/quick-news";
          } else {
            prevCard(eventHandler);
          }
        } else if (
          offsetY < -verticalThreshold &&
          currentIndex < cards.length - 1
        ) {
          nextCard(eventHandler);
        }
      }
      setOffsetY(0);
      setOffsetX(0);
    },
    [offsetX, offsetY, currentIndex, cards.length, prevCard, nextCard],
  );

  // Use effect to handle the Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (currentIndex < cards.length - 1 && onBoarded) {
          nextCard("keyboard");
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (currentIndex > 0 && onBoarded) {
          prevCard("keyboard");
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentIndex, cards.length, prevCard, nextCard, onBoarded]);

  // Use effect to handle the mouse wheel navigation for cards
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (isScrolling.current) return;
      isScrolling.current = true;
      if (e.deltaY > 0 && currentIndex < cards.length - 1) nextCard("scroll");
      else if (e.deltaY < 0 && currentIndex > 0) prevCard("scroll");
      setTimeout(() => (isScrolling.current = false), 200);
    };
    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [currentIndex, cards.length, prevCard, nextCard]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) handleEnd("mouse");
    };
    window.addEventListener("mouseup", handleGlobalMouseUp);
    return () => {
      window.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isDragging, handleEnd]);

  if (!isSuccess) {
    return <ErrorPage response={dataLoaderResponse} />;
  }

  return (
    <QuickNewsContainer>
      <QnOnboard
        isMobile={isMobile}
        initialId={initialId}
        setPushState={setPushState}
        article={articles[0] as ProcessedArticleData}
      />
      <QnTopNav />

      {/* Quick News Content */}
      <div
        ref={containerRef}
        className="qn-card-container relative w-full h-[calc(100svh-90px)] px-2.5 overflow-hidden cursor-grab active:cursor-grabbing select-none mt-4"
        style={{ touchAction: "none", overscrollBehavior: "none" }}
        onMouseDown={(e) => {
          handleStart(e.clientY, e.clientX);
        }}
        onMouseMove={(e) => {
          handleMove(e.clientY, e.clientX);
        }}
        onMouseUp={() => {
          handleEnd("mouse");
        }}
        onMouseLeave={() => {
          handleEnd("mouse");
        }}
        onTouchStart={(e) => {
          handleStart(e.touches[0].clientY, e.touches[0].clientX);
        }}
        onTouchMove={(e) => {
          handleMove(e.touches[0].clientY, e.touches[0].clientX);
        }}
        onTouchEnd={() => {
          handleEnd("touch");
        }}
      >
        <div className="qn-card-wrapper relative w-full md:w-[450px] h-full mx-auto">
          {articles.map((card, index) => (
            <div
              key={`qn-article-${card.id}`}
              className="absolute inset-0 flex items-center justify-center h-full md:h-[80svh]"
              style={{
                ...getCardStyle(
                  index,
                  articleIndex,
                  isDragging,
                  offsetY,
                  isMobile,
                  containerRef,
                ),
                transition: isDragging
                  ? "none"
                  : "all 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.175)",
                pointerEvents: index === articleIndex ? "auto" : "none",
              }}
            >
              <div className="w-full h-full rounded-3xl flex flex-col items-center justify-center overflow-hidden">
                {renderFeedCard({
                  card,
                  typeIndex: index,
                  isMobile,
                  nextCard,
                  getOlderArticles,
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <QnCardNav
        canPrevFeed={articleIndex > 0}
        canNextFeed={articleIndex < articles.length - 1}
        onPrev={prevCard}
        onNext={nextCard}
      />
      <QnFooter />
    </QuickNewsContainer>
  );
}

export async function loader({ params }: Route.LoaderArgs) {
  const id = params.id || undefined;
  const data = await fetchQNData({ id });

  return {
    ...data,
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as TRouteWithoutRedirect<
    QuickNewsDataPayload,
    string
  > | null;

  if (!result) {
    console.error(
      "Error occurred during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  // Check for valid payload field
  if (!("payload" in result)) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return result;
}
